import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ShieldOff, Plus, Trash2, AlertTriangle, Upload, FileSpreadsheet, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

const reasonStyle = {
  bounce: { bg: "#FEE2E2", text: "#991B1B" },
  complaint: { bg: "#FEF3C7", text: "#92400E" },
  unsubscribe: { bg: "#E0E7FF", text: "#3730A3" },
  manual: { bg: "#F3F4F6", text: "#404040" },
};

const TABS = [
  { id: "all", label: "All" },
  { id: "bounce", label: "Bounces" },
  { id: "unsubscribe", label: "Unsubscribes" },
  { id: "complaint", label: "Complaints" },
  { id: "manual", label: "Manual" },
  { id: "blocked-domains", label: "Blocked domains" },
];

export default function SuppressionsPage() {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "all";
  const isDomainsTab = tab === "blocked-domains";
  const showBounceReasonCol = tab === "bounce" || tab === "all";
  const [syncing, setSyncing] = useState(false);
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [domainSaving, setDomainSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", reason: "manual" });
  const [openImport, setOpenImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetcher = useCallback((page, limit) => {
    const params = { page, limit };
    if (tab !== "all") params.reason = tab;
    return api.get("/suppressions", { params });
  }, [tab]);

  const {
    items,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    reload,
    resetPage,
  } = usePaginatedList(fetcher, [tab]);

  useEffect(() => { resetPage(); }, [tab, resetPage]);

  const loadDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const { data } = await api.get("/domain-blacklist");
      setDomains(Array.isArray(data) ? data : data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load blocked domains");
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isDomainsTab) loadDomains();
  }, [isDomainsTab, loadDomains]);

  const setTab = (id) => {
    if (id === "all") setSearchParams({});
    else setSearchParams({ tab: id });
  };

  const syncFromCampaigns = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/suppressions/sync");
      const bf = data.backfill;
      const bfMsg = bf
        ? ` Reasons: ${bf.suppressions_updated ?? 0} suppression(s) updated${bf.still_missing ? `, ${bf.still_missing} still without text` : ""}.`
        : "";
      toast.success(`Synced: ${data.added} added, ${data.updated} updated.${bfMsg}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const add = async () => {
    if (!form.email) return toast.error("Email required");
    try {
      await api.post("/suppressions", form);
      toast.success("Added to suppression list");
      setOpen(false);
      setForm({ email: "", reason: "manual" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (tab !== "all") params.reason = tab;
      const res = await api.get("/suppressions/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mailwave-suppressions-${tab}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Suppression list exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const uploadCsv = async () => {
    if (!csvFile) return toast.error("Pick a CSV file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post("/suppressions/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.added} (${data.skipped} skipped)`);
      setCsvFile(null);
      setOpenImport(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addDomain = async () => {
    const raw = domainInput.trim();
    if (!raw) return toast.error("Domain required");
    setDomainSaving(true);
    try {
      const { data } = await api.post("/domain-blacklist", { domain: raw });
      const n = data.contacts_suspended ?? 0;
      toast.success(
        n > 0
          ? `Blocked ${data.item?.domain || raw} — ${n} contact(s) suspended`
          : `Blocked ${data.item?.domain || raw}`,
      );
      setDomainOpen(false);
      setDomainInput("");
      loadDomains();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to block domain");
    } finally {
      setDomainSaving(false);
    }
  };

  const removeDomain = async (entry) => {
    const ok = await confirm({
      title: "Unblock domain?",
      description: `Remove ${entry.domain} from the blocklist? Matching contacts that were suspended only for this domain will be set back to subscribed.`,
      confirmText: "Unblock",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/domain-blacklist/${entry.id}`);
      const n = data.contacts_restored ?? 0;
      toast.success(n > 0 ? `Unblocked — ${n} contact(s) re-subscribed` : "Domain unblocked");
      loadDomains();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Remove suppression?",
      description: "Remove from suppression list? This email will be eligible to receive emails again.",
      confirmText: "Remove",
    });
    if (!ok) return;
    try {
      await api.delete(`/suppressions/${id}`);
      toast.success("Removed");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="suppressions-page">
      <PageHeader
        title="Suppression list"
        subtitle={
          isDomainsTab
            ? "Block entire recipient domains (e.g. 163.com). Matching contacts are suspended and will not receive campaigns or automations."
            : "Bounces, unsubscribes, and complaints are synced from campaign activity. Use tabs to filter by type."
        }
        action={
          <div className="flex flex-wrap gap-2">
            {isDomainsTab ? (
              <Dialog open={domainOpen} onOpenChange={setDomainOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="add-blocked-domain-button">
                    <Plus className="h-4 w-4 mr-2" /> Block domain
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Block recipient domain</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Example: <code className="bg-neutral-100 dark:bg-neutral-900 px-1 rounded-sm">163.com</code> blocks all addresses like{" "}
                      <code className="bg-neutral-100 dark:bg-neutral-900 px-1 rounded-sm">user@163.com</code> and suspends existing contacts.
                    </p>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider">Domain</Label>
                      <Input
                        value={domainInput}
                        onChange={(e) => setDomainInput(e.target.value)}
                        placeholder="163.com"
                        className="mt-2 rounded-sm"
                        data-testid="blocked-domain-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDomainOpen(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={addDomain} disabled={domainSaving} className="rounded-sm btn-brand font-semibold" data-testid="blocked-domain-submit">
                      {domainSaving ? "Saving…" : "Block domain"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : (
              <>
            <Button
              variant="outline"
              onClick={syncFromCampaigns}
              disabled={syncing}
              className="rounded-sm h-11 font-semibold"
              data-testid="sync-suppressions-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing..." : "Sync from campaigns"}
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={exporting || total === 0}
              className="rounded-sm h-11 font-semibold"
              data-testid="export-suppressions-button"
            >
              <Download className={`h-4 w-4 mr-2 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Dialog open={openImport} onOpenChange={setOpenImport}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm h-11 font-semibold" data-testid="import-suppressions-button">
                  <Upload className="h-4 w-4 mr-2" /> Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Import suppressions from CSV</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Columns: <code className="bg-neutral-100 px-1 rounded-sm">email, reason</code> (optional).</p>
                  <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} className="rounded-sm" data-testid="suppression-csv-input" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenImport(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={uploadCsv} disabled={!csvFile || uploading} className="rounded-sm btn-brand font-semibold" data-testid="suppression-csv-submit">
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading..." : "Upload CSV"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="add-suppression-button">
                  <Plus className="h-4 w-4 mr-2" /> Add email
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add to suppression list</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm" data-testid="suppression-email-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={add} className="rounded-sm btn-brand font-semibold" data-testid="suppression-submit">Add</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
              </>
            )}
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-wrap gap-2 mb-6 border-b border-neutral-200 dark:border-neutral-800 pb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200"
              }`}
              data-testid={`suppression-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isDomainsTab ? (
          domainsLoading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
          ) : domains.length === 0 ? (
            <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
              <ShieldOff className="h-10 w-10 text-neutral-300 mx-auto mb-4" />
              <h3 className="font-display font-bold text-xl mb-2">No blocked domains</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                Add domains you never want to mail (competitors, problematic providers, etc.).
              </p>
              <Button onClick={() => setDomainOpen(true)} className="rounded-sm btn-brand font-semibold" data-testid="blocked-domain-empty-add">
                <Plus className="h-4 w-4 mr-2" /> Block domain
              </Button>
            </div>
          ) : (
            <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
              <table className="w-full min-w-[480px]">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                    <th className="text-left px-6 py-3 label-overline">Domain</th>
                    <th className="text-left px-6 py-3 label-overline">Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d) => (
                    <tr key={d.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-900" data-testid={`blocked-domain-row-${d.id}`}>
                      <td className="px-6 py-4 text-sm font-medium font-mono">{d.domain}</td>
                      <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">{d.added_at ? new Date(d.added_at).toLocaleDateString() : "—"}</td>
                      <td className="px-6 py-4 text-right">
                        <button type="button" onClick={() => removeDomain(d)} className="text-neutral-400 hover:text-red-600" data-testid={`blocked-domain-delete-${d.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
            <ShieldOff className="h-10 w-10 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No entries in this tab</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
              Click <strong>Sync from campaigns</strong> to import bounces and unsubscribes from your sent campaigns.
            </p>
            <Button onClick={syncFromCampaigns} disabled={syncing} className="rounded-sm btn-brand font-semibold">
              <RefreshCw className="h-4 w-4 mr-2" /> Sync from campaigns
            </Button>
          </div>
        ) : (
          <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                  <th className="text-left px-6 py-3 label-overline">Email</th>
                  <th className="text-left px-6 py-3 label-overline">Type</th>
                  {showBounceReasonCol && <th className="text-left px-6 py-3 label-overline">Bounce reason</th>}
                  <th className="text-left px-6 py-3 label-overline">Campaign</th>
                  <th className="text-left px-6 py-3 label-overline">Scope</th>
                  <th className="text-left px-6 py-3 label-overline">Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const r = reasonStyle[s.reason] || reasonStyle.manual;
                  return (
                    <tr key={s.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-900" data-testid={`suppression-row-${s.id}`}>
                      <td className="px-6 py-4 text-sm font-medium">{s.email}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: r.bg, color: r.text }}>{s.reason}</span>
                      </td>
                      {showBounceReasonCol && (
                        <td className="px-6 py-4 text-xs text-neutral-600 dark:text-neutral-400 max-w-xs truncate" title={s.bounce_reason}>
                          {s.reason === "bounce" ? (s.bounce_reason || "—") : "—"}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{s.campaign_name || "—"}</td>
                      <td className="px-6 py-4 text-xs text-neutral-500 dark:text-neutral-400">
                        {s.user_id === null ? (
                          <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Global</span>
                        ) : "Yours"}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">{new Date(s.added_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-right">
                        <button type="button" onClick={() => remove(s.id)} className="text-neutral-400 hover:text-red-600" data-testid={`suppression-delete-${s.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ListPagination
              page={page}
              pages={pages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
